import React, { useState } from 'react';
import { Lock, Loader2, CreditCard, AlertCircle } from 'lucide-react';
import { fmtMoney } from '../../lib/money';
import { TrustRow } from './CheckoutExtras';

/** "[Refund Policy](https://…)" markdown links → anchors. */
export function ConsentText({ text }) {
  const parts = String(text || '').split(/(\[[^\]]+\]\([^)]+\))/g).filter(Boolean);
  return (
    <>
      {parts.map((p, i) => {
        const m = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (!m) return <span key={i}>{p}</span>;
        return <a key={i} href={m[2]} target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-900 underline underline-offset-2 hover:text-amber-700">{m[1]}</a>;
      })}
    </>
  );
}

export function payLabel(cfg, quote) {
  const total = quote ? fmtMoney(quote.total, quote.currency, quote.divisor) : '';
  const tpl = cfg.pay_button_label || 'Pay {total}';
  return tpl.includes('{total}') ? tpl.replace('{total}', total).trim() : `${tpl} ${total}`.trim();
}

/**
 * Right column: account email, payment fields (children), refund-policy
 * consent, Pay button, reassurance copy.
 * Below `lg` the consent + Pay live in the sticky MobilePayBar instead, so the
 * panel only shows what the student must actually fill in.
 */
export function PaymentPanel({ cfg, quote, email, consent, onConsent, onPay, paying, payError, canPay, children, ctaLabel, ctaHint }) {
  const disabled = !canPay || paying || !consent || !quote;
  const trust = (cfg.trust_items || []).filter(Boolean);
  return (
    <section className="rounded-3xl bg-white border border-slate-200/80 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.35)] p-4 sm:p-7" data-testid="payment-panel">
      <div className="hidden sm:block mb-6">
        <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400">{cfg.label_account || 'Your account'}</p>
        <div className="mt-2 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3">
          <span className="w-9 h-9 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center justify-center shrink-0">{(email || '?').charAt(0).toUpperCase()}</span>
          <div className="min-w-0">
            <p className="text-xs text-slate-500">{cfg.label_account_hint || 'Access will be activated on'}</p>
            <p className="text-sm font-semibold text-slate-900 truncate" data-testid="checkout-email">{email}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400">{cfg.label_payment_details || 'Payment details'}</p>
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500"><Lock size={11} />{cfg.label_encrypted || 'Encrypted by Stripe'}</span>
      </div>
      <div data-testid="payment-fields">{children}</div>
      <p className="sm:hidden mt-3 text-xs text-slate-500 truncate" data-testid="checkout-email-compact">{cfg.label_account_compact || 'Access activates on'} <span className="font-semibold text-slate-800">{email}</span></p>

      <label className="mt-5 hidden lg:flex items-start gap-3 cursor-pointer select-none" data-testid="consent-label">
        <input type="checkbox" checked={consent} onChange={(e) => onConsent(e.target.checked)} className="mt-0.5 w-[18px] h-[18px] rounded border-slate-300 text-slate-900 focus:ring-slate-900" data-testid="consent-checkbox" />
        <span className="text-sm text-slate-600 leading-snug"><ConsentText text={cfg.consent_message} /></span>
      </label>

      {payError && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700" data-testid="pay-error" role="alert">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />{payError}
        </div>
      )}

      <button type="button" onClick={onPay} disabled={disabled} data-testid="pay-button"
        className="mt-5 w-full min-h-[56px] rounded-2xl bg-slate-900 text-white font-bold text-base hidden lg:flex items-center justify-center gap-2 hover:bg-slate-800 hover:-translate-y-px active:translate-y-0 transition-[background-color,transform,opacity] disabled:opacity-40 disabled:hover:translate-y-0 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.6)]">
        {paying ? <Loader2 size={20} className="animate-spin" /> : <><Lock size={16} className="text-amber-400" />{ctaLabel || payLabel(cfg, quote)}</>}
      </button>
      {ctaHint && <p className="hidden lg:block text-xs text-slate-500 text-center mt-2.5">{ctaHint}</p>}
      {cfg.pay_subtext && <p className="hidden lg:flex text-xs text-slate-500 text-center mt-2.5 items-center justify-center gap-1.5" data-testid="pay-subtext"><CreditCard size={12} />{cfg.pay_subtext}</p>}
      <div className="hidden lg:block mt-5"><TrustRow cfg={cfg} /></div>
      {trust.length > 0 && (
        <p className="lg:hidden mt-4 text-[11px] font-semibold text-slate-500 text-center flex items-center justify-center gap-1.5" data-testid="trust-row-compact">
          <Lock size={11} className="text-slate-400 shrink-0" /><span className="truncate">{trust.join(' · ')}</span>
        </p>
      )}
    </section>
  );
}

/**
 * Sticky mobile bar (below `lg`): refund-policy consent + total + Pay are all
 * one thumb away. Pay stays tappable — without consent it explains what's
 * missing instead of sitting greyed out.
 */
export function MobilePayBar({ cfg, quote, onPay, paying, disabled, consent, onConsent, ctaLabel }) {
  const [needConsent, setNeedConsent] = useState(false);
  if (!quote) return null;
  const tap = () => {
    if (!consent) { setNeedConsent(true); return; }
    setNeedConsent(false);
    onPay();
  };
  const toggle = (v) => { setNeedConsent(false); onConsent?.(v); };
  const label = ctaLabel || (cfg.mobile_pay_button_label ? payLabel({ pay_button_label: cfg.mobile_pay_button_label }, quote) : payLabel(cfg, quote));
  return (
    <div className="lg:hidden fixed bottom-0 inset-x-0 z-[110] border-t border-slate-200 bg-white/95 backdrop-blur px-4 pt-2.5 pb-3 shadow-[0_-10px_30px_-20px_rgba(15,23,42,0.4)]" data-testid="mobile-pay-bar">
      <label className={`flex items-start gap-2.5 cursor-pointer select-none rounded-xl px-2 py-1.5 -mx-2 transition-colors ${needConsent ? 'bg-rose-50' : ''}`} data-testid="mobile-consent-label">
        <input type="checkbox" checked={!!consent} onChange={(e) => toggle(e.target.checked)}
          className="mt-0.5 w-[18px] h-[18px] rounded border-slate-300 text-slate-900 focus:ring-slate-900 shrink-0" data-testid="mobile-consent-checkbox" />
        <span className={`text-xs leading-snug ${needConsent ? 'text-rose-700' : 'text-slate-600'}`}>
          <ConsentText text={cfg.consent_message} />
          {needConsent && <span className="block font-semibold mt-0.5" data-testid="mobile-consent-error">{cfg.mobile_consent_error || 'Tick the box to continue to payment.'}</span>}
        </span>
      </label>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold tracking-widest uppercase text-slate-400">{cfg.label_total_due || 'Total due today'}</p>
          <p className="text-lg font-extrabold text-slate-900 leading-none mt-0.5" data-testid="mobile-total">{fmtMoney(quote.total, quote.currency, quote.divisor)}</p>
        </div>
        <button type="button" onClick={tap} disabled={disabled || paying} data-testid="mobile-pay-button"
          className="rounded-2xl bg-slate-900 text-white font-bold text-sm px-5 min-h-[48px] flex items-center gap-2 disabled:opacity-40">
          {paying ? <Loader2 size={18} className="animate-spin" /> : <><Lock size={14} className="text-amber-400" />{label}</>}
        </button>
      </div>
    </div>
  );
}
